/**
 * The learner session kept on this device (D-16), per parent user, so a
 * reload or a returning learner can resume without the PIN while the session
 * is still valid.
 *
 * Not proof of anything: the server decides whether the token is a live
 * session of this parent login (`ping_learner_session`) and which learner it
 * belongs to. The stored learner id only preselects the profile on the
 * learner-selection screen. The token is a bearer secret with the same
 * storage exposure as the Supabase session already kept in `localStorage`;
 * it expires server-side (idle and maximum lifetime) and is revoked on
 * switch, exit and sign-out.
 */

export interface StoredLearnerSession {
  readonly learnerId: string
  readonly token: string
}

export interface ChildModeStore {
  load(userId: string): StoredLearnerSession | null
  save(userId: string, session: StoredLearnerSession): void
  clear(userId: string): void
}

const key = (userId: string) => `alp.learner-session.${userId}`
/** Pre-D-16 key: only a selected learner id, never a credential. Removed on sight. */
const legacyKey = (userId: string) => `alp.child-mode.${userId}`

function parse(raw: string | null): StoredLearnerSession | null {
  if (raw === null) return null
  try {
    const value = JSON.parse(raw) as Partial<StoredLearnerSession>
    return typeof value.learnerId === 'string' &&
      typeof value.token === 'string' &&
      /^[0-9a-f]{64}$/.test(value.token)
      ? { learnerId: value.learnerId, token: value.token }
      : null
  } catch {
    return null
  }
}

export function createChildModeStore(storage: Storage): ChildModeStore {
  return {
    load: (userId) => {
      try {
        storage.removeItem(legacyKey(userId))
        return parse(storage.getItem(key(userId)))
      } catch {
        return null
      }
    },
    save: (userId, session) => {
      try {
        storage.setItem(key(userId), JSON.stringify(session))
      } catch {
        // Storage unavailable (private mode quota): the session still works
        // for this page view; it just will not survive a reload.
      }
    },
    clear: (userId) => {
      try {
        storage.removeItem(key(userId))
        storage.removeItem(legacyKey(userId))
      } catch {
        // See save().
      }
    },
  }
}
